import React from 'react';
import {
	type AttributeAction,
	type AttributePermissions,
	AttributeSkeleton,
	type AttributeSkeletonProps,
	type AttributesWorking,
	type IAttributeTemplate,
	type Lookups,
	ReactHookFormAttributeContainer,
	type ReactHookFormAttributeContainerProps,
} from '@netx/core-attribute';
import {EmptyStateFallbackMessage} from '@netx/core-components/EmptyStateFallbackMessage';
import type {FormValues} from '@netx/core-components/Form/hooks/useForm';
import {isEmptyValue} from '@netx/core-components/Form/utils';
import {i18n} from '@netx/core/i18n';
import type {AttributeSetId} from '../../types';

export interface AttributeSetProps extends Omit<
	ReactHookFormAttributeContainerProps,
	'attrData' | 'multipleValues' | 'onActionChange'
> {
	allowSystemAttributes?: boolean;
	hideReadOnlyAttributes?: boolean;
	hideReadOnlyAttributesWithoutValues?: boolean;
	attributes: IAttributeTemplate[];
	attributesWorking?: AttributesWorking;
	id: AttributeSetId;
	limitToValues?: boolean;
	loading?: boolean;
	loadingValues?: boolean;
	lookupsAllowed?: Lookups;
	lookupsDisabled?: boolean;
	multipleValues?: Record<number, boolean>;
	onActionChange?: (attributeId: number, action: AttributeAction) => void;
	onLookup?: (
		attribute: IAttributeTemplate,
		lookupValue: string,
	) => Promise<void | any>;
	originalValues?: FormValues;
	permissions?: AttributePermissions;
	readOnlyAttributes?: Record<number, boolean>;
}

export const AttributeSetPrimitive = ({
	allowSystemAttributes,
	appending,
	attributes = [],
	attributesWorking,
	control,
	hideReadOnlyAttributes,
	hideReadOnlyAttributesWithoutValues,
	id,
	limitToValues,
	loading,
	loadingValues,
	lookupsAllowed,
	lookupsDisabled,
	multipleValues,
	onLookup,
	onActionChange,
	originalValues,
	permissions,
	readOnly,
	readOnlyAttributes,
	useActions,
	...props
}: AttributeSetProps) => {
	// TODO: move this logic up higher to the attribute context
	const canMetadataLookup = onLookup != null;

	// const mer = useWatch
	// console.log(originalValues, props);
	const ids = attributes.map((attr) => attr.attributeid); // Object.keys(values || {}).map((id) => parseInt(id.toString()));
	const displayableAttributes = attributes.filter((attr) => {
		const permReadOnly =
			readOnlyAttributes?.[attr.attributeid] === true ||
			// TODO: this disconnect is exhausting - in some cases `r|w|d` and others `true|false`
			(attr as any).permission === false ||
			attr.permission === 'r';
		return (
			(!limitToValues || ids.includes(attr.attributeid)) &&
			(allowSystemAttributes || !attr.system) &&
			(!hideReadOnlyAttributesWithoutValues ||
				(!permReadOnly && !readOnly) ||
				!isEmptyValue(originalValues?.[attr.attributeid])) &&
			((!permReadOnly && !readOnly) || !hideReadOnlyAttributes)
		);
	});

	if (
		((limitToValues || (readOnly && hideReadOnlyAttributesWithoutValues)) &&
			loadingValues) ||
		loading
	) {
		return (
			<>
				{[0, 1, 2].map((val) => (
					<AttributeSkeleton key={val} {...(props as AttributeSkeletonProps)} />
				))}
			</>
		);
	}

	if (!attributes.length) {
		return <EmptyStateFallbackMessage title={i18n.t('i18n.noAttributes')} />;
	}

	if (!displayableAttributes.length) {
		return (
			<EmptyStateFallbackMessage
				title={i18n.t('i18n.noAttributesAvailableInThisContext')}
			/>
		);
	}

	return (
		<>
			{displayableAttributes.map((attr) => {
				return (
					<ReactHookFormAttributeContainer
						attributeWorking={attributesWorking?.[attr.attributeid]}
						control={control}
						onActionChange={(action) => {
							onActionChange?.(attr.attributeid, action);
						}}
						isMetadataLookup={
							!lookupsDisabled &&
							canMetadataLookup &&
							lookupsAllowed?.keys.includes(attr.attributeid)
						}
						isMetadataTarget={
							!lookupsDisabled &&
							canMetadataLookup &&
							lookupsAllowed?.targets.includes(attr.attributeid)
						}
						onLookup={canMetadataLookup ? onLookup : undefined}
						loadingValue={loadingValues}
						readOnly={
							readOnly ||
							permissions?.[attr.attributeid] === 'r' ||
							readOnlyAttributes?.[attr.attributeid] === true
						}
						multipleValues={multipleValues?.[attr.attributeid]}
						{...props}
						appending={appending}
						attrData={attr}
						key={attr.attributeid}
						useActions={useActions}
					/>
				);
			})}
		</>
	);
};

export const AttributeSet = AttributeSetPrimitive;

export default AttributeSet;
